import React, { useId } from 'react'
import PropTypes from 'prop-types'
import './EdcLoader.css'

/**
 * Consistent brand loader used across ShortCircuit.
 *
 * The EDC wordmark is rendered inline as SVG rectangles (avoiding any
 * external mask/URL plumbing) and filled with a `<linearGradient>` whose
 * stops animate horizontally — producing a wave that sweeps across the
 * mark. A soft brand-blue halo sits behind it so the loader is visible
 * even on busy backgrounds.
 */
const SIZES = {
  small:  { w: 88,  labelClass: 'edc-loader__label--sm' },
  medium: { w: 144, labelClass: 'edc-loader__label--md' },
  large:  { w: 220, labelClass: 'edc-loader__label--lg' },
}

const ASPECT = 21.209 / 100.206 // SVG native aspect ratio.

// Coordinates of the 9 rects in the EDC wordmark (viewBox 100.206 x 21.209).
const EDC_RECTS = [
  { x: 0,      y: 9.159,  w: 28.008, h: 2.891 },
  { x: 0,      y: 0,      w: 28.008, h: 2.891 },
  { x: 0,      y: 18.318, w: 28.008, h: 2.891 },
  { x: 61.841, y: 0,      w: 2.891,  h: 21.209 },
  { x: 70.95,  y: 0,      w: 2.891,  h: 21.209 },
  { x: 36.1,   y: 0,      w: 28.008, h: 2.891 },
  { x: 36.1,   y: 18.318, w: 28.008, h: 2.891 },
  { x: 72.198, y: 0,      w: 28.008, h: 2.891 },
  { x: 72.198, y: 18.318, w: 28.008, h: 2.891 },
]

const VARIANT_STOPS = {
  // Cool slate base + brand-blue highlight — pops on white / light surfaces.
  neutral: {
    base: '#475569',
    mid:  '#74b9ff',
    peak: '#4a9ce6',
  },
  // Bright base + pure-white peak — pops on dark gradient surfaces.
  light: {
    base: '#cbd5e1',
    mid:  '#ffffff',
    peak: '#f8fafc',
  },
}

const EdcLoader = ({
  size = 'large',
  label = null,
  className = '',
  variant = 'neutral',
}) => {
  const cfg = SIZES[size] || SIZES.large
  const w = cfg.w
  const h = Math.round(w * ASPECT)
  const gradId = useId().replace(/:/g, '') + '-edc-wave'
  const stops = VARIANT_STOPS[variant] || VARIANT_STOPS.neutral

  return (
    <div
      className={['edc-loader-wrap', className || ''].join(' ').trim()}
      role="status"
      aria-live="polite"
    >
      <div className="edc-loader-stage" style={{ width: w, height: h }}>
        <span className="edc-loader__halo" aria-hidden />
        <svg
          className={['edc-loader', `edc-loader--${variant}`].join(' ')}
          width={w}
          height={h}
          viewBox="0 0 100.206 21.209"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={stops.base}>
                <animate
                  attributeName="offset"
                  values="-0.4; 0.6; 1.4"
                  dur="1.6s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="0%" stopColor={stops.mid}>
                <animate
                  attributeName="offset"
                  values="-0.25; 0.75; 1.55"
                  dur="1.6s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="0%" stopColor={stops.peak}>
                <animate
                  attributeName="offset"
                  values="-0.18; 0.82; 1.62"
                  dur="1.6s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="0%" stopColor={stops.mid}>
                <animate
                  attributeName="offset"
                  values="-0.1; 0.9; 1.7"
                  dur="1.6s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="0%" stopColor={stops.base}>
                <animate
                  attributeName="offset"
                  values="0; 1; 1.85"
                  dur="1.6s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>
          <g fill={`url(#${gradId})`}>
            {EDC_RECTS.map((r, i) => (
              <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} />
            ))}
          </g>
        </svg>
      </div>
      {label ? (
        <div className={['edc-loader__label', cfg.labelClass].join(' ')}>{label}</div>
      ) : (
        <span className="edc-loader__sr">Loading…</span>
      )}
    </div>
  )
}

EdcLoader.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  label: PropTypes.string,
  className: PropTypes.string,
  /**
   * Color palette of the wave.
   * - `neutral`: slate base + brand-blue highlight (fits light surfaces).
   * - `light`:   pale base + white highlight (fits dark surfaces).
   */
  variant: PropTypes.oneOf(['neutral', 'light']),
}

export default EdcLoader
